<template>
  <div class="catalog-card-list-skeleton">
    <div v-for="n in count" :key="n" class="catalog-card-item-skeleton">
      <div class="card-inner">
        <!-- Image Skeleton -->
        <div class="card-image-wrapper">
          <div class="skeleton-image"></div>
        </div>

        <!-- Content Skeleton -->
        <div class="card-content">
          <!-- Title Skeleton -->
          <div class="skeleton-title"></div>

          <!-- Actions Skeleton -->
          <div class="card-actions">
            <div class="skeleton-button"></div>
            <div class="skeleton-button short"></div>
          </div>

          <!-- Meta Skeleton -->
          <div class="card-meta">
            <div class="skeleton-badge"></div>
            <div class="skeleton-type"></div>
          </div>

          <!-- Description Skeleton -->
          <div class="skeleton-description"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "CatalogCardListSkeleton",
  props: {
    count: {
      type: Number,
      default: 5
    }
  }
}
</script>

<style scoped>
.catalog-card-list-skeleton {
  background: #fff;
}

.catalog-card-item-skeleton {
  border-bottom: 1px solid #f0f0f0;
}

.catalog-card-item-skeleton:last-child {
  border-bottom: none;
}

.card-inner {
  display: flex;
  gap: 20px;
  padding: 20px;
}

/* Image Skeleton */
.card-image-wrapper {
  flex-shrink: 0;
  width: 200px;
}

.skeleton-image {
  width: 100%;
  height: 140px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 8px;
}

/* Content Skeleton */
.card-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.skeleton-title {
  width: 70%;
  height: 22px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

/* Actions Skeleton */
.card-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
  margin-left: auto;
}

.skeleton-button {
  width: 100px;
  height: 32px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 6px;
}

.skeleton-button.short {
  width: 70px;
}

/* Meta Skeleton */
.card-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.skeleton-badge {
  width: 120px;
  height: 24px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-type {
  width: 80px;
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

/* Description Skeleton */
.skeleton-description {
  width: 90%;
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* GPU-accelerate skeleton shimmer */
.skeleton-image,
.skeleton-title,
.skeleton-button,
.skeleton-badge,
.skeleton-type,
.skeleton-description {
  will-change: background-position;
}

/* Responsive - Tablet */
@media (max-width: 959px) {
  .card-inner {
    gap: 16px;
    padding: 16px;
  }

  .card-image-wrapper {
    width: 160px;
  }

  .skeleton-image {
    height: 120px;
  }

  .skeleton-title {
    height: 20px;
  }

  .skeleton-button {
    width: 80px;
    height: 28px;
  }

  .skeleton-button.short {
    width: 60px;
  }
}

/* Responsive - Mobile */
@media (max-width: 639px) {
  .catalog-card-list-skeleton {
    background: transparent;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .catalog-card-item-skeleton {
    background: #ffffff;
    border-radius: 14px;
    border-bottom: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  .card-inner {
    flex-direction: column;
    gap: 12px;
    padding: 14px;
  }

  .card-image-wrapper {
    width: 100%;
  }

  .skeleton-image {
    height: 180px;
    width: 100%;
  }

  .card-content {
    gap: 10px;
  }

  .skeleton-title {
    width: 100%;
    height: 20px;
  }

  .card-actions {
    gap: 6px;
    justify-content: flex-end;
  }

  .skeleton-button {
    flex: 0 0 auto;
    width: 90px;
    height: 36px;
  }

  .skeleton-button.short {
    width: 70px;
  }

  .card-meta {
    gap: 8px;
  }

  .skeleton-badge {
    width: 100px;
    height: 22px;
  }

  .skeleton-description {
    height: 14px;
  }
}

/* Responsive - Small Mobile */
@media (max-width: 479px) {
  .catalog-card-list-skeleton {
    gap: 10px;
  }

  .catalog-card-item-skeleton {
    border-radius: 14px;
  }

  .card-inner {
    padding: 12px;
  }

  .skeleton-image {
    height: 160px;
  }

  .card-actions {
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .skeleton-button {
    flex: 0 0 auto;
  }
}

@media (max-width: 639px) {
  .catalog-card-item-skeleton {
    overflow: hidden;
    border-radius: 12px;
  }

  .card-inner {
    gap: 12px;
    padding: 12px;
  }

  .card-image-wrapper {
    width: 100%;
    aspect-ratio: 16 / 9;
  }

  .skeleton-image {
    height: 100%;
    min-height: 0;
    border-radius: 10px;
  }

  .skeleton-button {
    min-height: 44px;
  }
}
</style>
